import { RefreshCw, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { LOADER_COLORS } from '@/components/loading/types';

interface AppRecoveryScreenProps {
  error: Error;
  onRetry: () => void;
  onReload?: () => void;
  retryCount?: number;
}

/**
 * Full-page recovery screen for critical app failures
 *
 * Matches the Spirit of Shabbat design language with:
 * - Purple gradient background
 * - Merriweather headings
 * - Friendly, non-technical messaging
 * - Expandable diagnostics for debugging
 */
export function AppRecoveryScreen({
  error,
  onRetry,
  onReload = () => window.location.reload(),
  retryCount = 0,
}: AppRecoveryScreenProps) {
  const [showDetails, setShowDetails] = useState(false);

  // Escalate messaging based on retry count
  const getMessage = () => {
    if (retryCount >= 3) {
      return {
        title: "We're having trouble connecting",
        subtitle: 'Please try reloading the page or check your internet connection.',
        showReload: true,
      };
    }
    if (retryCount >= 1) {
      return {
        title: 'Still having issues',
        subtitle: "Let's try one more time.",
        showReload: true,
      };
    }
    return {
      title: 'Something unexpected happened',
      subtitle: "Don't worry, we can try to fix this.",
      showReload: false,
    };
  };

  const { title, subtitle, showReload } = getMessage();

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{
        background: `linear-gradient(135deg, ${LOADER_COLORS.primaryWhisper} 0%, ${LOADER_COLORS.primaryGhost} 50%, ${LOADER_COLORS.primaryFaint} 100%)`,
      }}
    >
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mb-6">
          <div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full"
            style={{ backgroundColor: LOADER_COLORS.primaryPale }}
          >
            <AlertTriangle size={40} color={LOADER_COLORS.primary} />
          </div>
        </div>

        {/* Message */}
        <h1
          className="text-2xl font-bold mb-2"
          style={{ color: LOADER_COLORS.primaryDeep, fontFamily: "'Merriweather', serif" }}
        >
          {title}
        </h1>
        <p className="text-slate-600 mb-8">{subtitle}</p>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onRetry}
            className="w-full py-3 px-6 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{ backgroundColor: LOADER_COLORS.primary }}
          >
            <RefreshCw size={20} />
            Try Again
          </button>

          {showReload && (
            <button
              onClick={onReload}
              className="w-full py-3 px-6 rounded-xl font-medium border-2 transition-all hover:bg-white/50"
              style={{ borderColor: LOADER_COLORS.primary, color: LOADER_COLORS.primary }}
            >
              Reload Page
            </button>
          )}
        </div>

        {/* Expandable diagnostics */}
        <div className="mt-8">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sm text-slate-500 flex items-center justify-center gap-1 mx-auto hover:text-slate-700"
          >
            Technical Details
            {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {showDetails && (
            <div className="mt-3 p-4 bg-white/50 rounded-lg text-left text-xs font-mono text-slate-600 break-all">
              <div>
                <strong>Error:</strong> {error.name}
              </div>
              <div>
                <strong>Message:</strong> {error.message}
              </div>
              {error.stack && (
                <div className="mt-2">
                  <strong>Stack:</strong>
                  <pre className="mt-1 text-[10px] overflow-auto max-h-32">{error.stack}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
